import { useCallback, useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface MediaUploadProps {
  value?: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<{ media_url: string }>;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
  isUploading?: boolean;
}

export function MediaUpload({
  value,
  onChange,
  onUpload,
  onError,
  className,
  disabled = false,
  isUploading = false,
}: MediaUploadProps) {
  const [preview, setPreview] = useState<string | null>(value || null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        onError?.('Only JPEG and PNG files are allowed');
        return;
      }

      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        onError?.('File size must be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file
      try {
        const response = await onUpload(file);
        onChange(response.media_url);
      } catch (error) {
        onError?.('Failed to upload image');
        setPreview(null);
      }
    },
    [onChange, onError, onUpload]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile, disabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleRemove = useCallback(() => {
    setPreview(null);
    onChange('');
  }, [onChange]);

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all',
        isDragging && 'border-primary ring-2 ring-primary/20',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      data-testid="media-upload-container"
    >
      {preview ? (
        <div className="relative aspect-video">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
            data-testid="media-upload-preview"
          />
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={handleRemove}
              data-testid="media-upload-remove"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <label
          htmlFor="media-upload-input"
          className={cn(
            'flex flex-col items-center justify-center p-8 cursor-pointer',
            disabled && 'cursor-not-allowed'
          )}
        >
          <input
            id="media-upload-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileInput}
            disabled={disabled || isUploading}
            className="sr-only"
            data-testid="media-upload-input"
          />
          <div className="text-center">
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-4 p-3 rounded-full bg-muted">
                  {isDragging ? (
                    <ImageIcon className="h-8 w-8 text-primary" />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <p
                  className="text-sm font-medium mb-1"
                  data-testid="media-upload-label"
                >
                  {isDragging
                    ? 'Drop image here'
                    : 'Click to upload or drag and drop'}
                </p>
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="media-upload-hint"
                >
                  JPEG or PNG, max 5MB
                </p>
              </>
            )}
          </div>
        </label>
      )}
    </Card>
  );
}
